import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BarChart3, Download, Phone, Plus, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, EmptyState, Skeleton } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { Avatar } from '@/components/ui/avatar';
import { Modal } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { api, cn, formatCurrency } from '@/lib/utils';

function RiskBadge({ level }: { level: 'low' | 'medium' | 'high' }) {
  const styles = {
    low: 'bg-[#DCFCE7] text-[#166534]',
    medium: 'bg-[#FEF9C3] text-[#854D0E]',
    high: 'bg-[#FEE2E2] text-[#991B1B]',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium capitalize', styles[level])}>{level} risk</span>
  );
}

interface TechnicianInsight {
  id: string;
  name: string;
  active: boolean;
  activeAgreements: number;
  outcomesInPeriod: number;
  renewals: number;
  lapses: number;
  renewalRate: number | null;
  churnRate: number | null;
  revenueRetained: number;
  avgRiskScore: number | null;
  insufficientData: boolean;
}

interface InsightsReport {
  periodMonths: number;
  minSampleSize: number;
  technicians: TechnicianInsight[];
  totals: { renewals: number; lapses: number; outcomes: number; revenueRetained: number };
}

interface AtRiskItem {
  id: string;
  contactName: string;
  contactPhone: string | null;
  planName: string;
  amount: number;
  relativeEndDate: string;
  risk: { level: 'low' | 'medium' | 'high'; score: number };
}

const PERIODS = [
  { label: '6 months', value: '6m' },
  { label: '12 months', value: '12m' },
  { label: '24 months', value: '24m' },
];

export function InsightsPage() {
  const qc = useQueryClient();
  const [period, setPeriod] = useState('12m');
  const [selected, setSelected] = useState<TechnicianInsight | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [techName, setTechName] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['technician-insights', period],
    queryFn: () => api<InsightsReport>(`/api/v1/insights/technicians?period=${period}`),
  });

  const { data: atRisk, isLoading: atRiskLoading } = useQuery({
    queryKey: ['technician-at-risk', selected?.id],
    queryFn: () => api<{ items: AtRiskItem[] }>(`/api/v1/insights/technicians/${selected!.id}/at-risk`),
    enabled: !!selected,
  });

  const addTech = useMutation({
    mutationFn: () => api('/api/v1/technicians', { method: 'POST', body: JSON.stringify({ name: techName }) }),
    onSuccess: () => {
      setAddOpen(false);
      setTechName('');
      qc.invalidateQueries({ queryKey: ['technician-insights'] });
    },
  });

  const ranked = useMemo(
    () => (data?.technicians ?? []).filter((t) => !t.insufficientData),
    [data?.technicians],
  );

  const exportCsv = () => {
    if (!data?.technicians.length) return;
    const header = 'Technician,Active agreements,Outcomes,Renewals,Lapses,Renewal rate,Non-renewal rate,Revenue retained,Avg risk,Status';
    const lines = data.technicians.map((t) =>
      [
        t.name,
        t.activeAgreements,
        t.outcomesInPeriod,
        t.renewals,
        t.lapses,
        t.renewalRate ?? 'n/a',
        t.churnRate ?? 'n/a',
        t.revenueRetained,
        t.avgRiskScore ?? 'n/a',
        t.insufficientData ? 'Not enough data' : 'Ranked',
      ].join(','),
    );
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `technician-insights-${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <Card className="p-6">
        <p className="text-sm text-text-secondary">
          Technician renewal coaching is available on the Pro plan — see which techs&apos; customers renew best and coach
          where it helps.
        </p>
        <Link to="/settings/billing" className="mt-3 inline-block text-sm font-medium text-brand hover:underline">
          View plans
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Technician renewals"
        description="Coaching view — who keeps customers renewing, where to support the team. Not a performance scorecard."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={exportCsv} disabled={!data?.technicians.length}>
              <Download className="h-4 w-4" /> Export CSV
            </Button>
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" /> Add technician
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setPeriod(p.value)}
            className={cn(
              'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              period === p.value ? 'bg-brand text-white' : 'bg-bg-subtle text-text-secondary hover:text-text-primary',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Card className="p-5">
          <p className="text-[13px] font-medium text-text-secondary">Team renewals</p>
          <p className="mt-1 text-2xl font-semibold">{isLoading ? '—' : data!.totals.renewals}</p>
        </Card>
        <Card className="p-5">
          <p className="text-[13px] font-medium text-text-secondary">Team lapses</p>
          <p className="mt-1 text-2xl font-semibold">{isLoading ? '—' : data!.totals.lapses}</p>
        </Card>
        <Card className="p-5">
          <p className="text-[13px] font-medium text-text-secondary">Revenue retained</p>
          <p className="mt-1 text-2xl font-semibold text-brand">
            {isLoading ? '—' : formatCurrency(data!.totals.revenueRetained)}
          </p>
        </Card>
        <Card className="p-5">
          <p className="text-[13px] font-medium text-text-secondary">Ranked technicians</p>
          <p className="mt-1 text-2xl font-semibold">{isLoading ? '—' : ranked.length}</p>
        </Card>
      </div>

      <Card
        title="Renewal coaching board"
        description={`Technicians need at least ${data?.minSampleSize ?? 10} outcomes in the period before we rank them.`}
        noPadding
      >
        {isLoading ? (
          <div className="divide-y divide-border px-5 py-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="mb-3 h-16 w-full" />
            ))}
          </div>
        ) : data?.technicians.length ? (
          <div className="divide-y divide-border">
            {data.technicians.map((tech, idx) => (
              <div key={tech.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
                <div className="flex w-8 shrink-0 justify-center text-sm font-semibold text-text-muted">
                  {tech.insufficientData ? '—' : idx + 1 - (data.technicians.filter((t, i) => i < idx && t.insufficientData).length)}
                </div>
                <Avatar name={tech.name} className="h-9 w-9" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-text-primary">{tech.name}</p>
                    {tech.insufficientData && (
                      <span className="rounded-full bg-bg-subtle px-2 py-0.5 text-[11px] font-medium text-text-secondary">
                        Not enough data ({tech.outcomesInPeriod}/{data.minSampleSize})
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-[13px] text-text-secondary">
                    {tech.activeAgreements} active agreements
                    {tech.avgRiskScore != null ? ` · avg risk ${tech.avgRiskScore}` : ''}
                  </p>
                </div>
                {tech.insufficientData ? (
                  <p className="text-sm text-text-muted">Assign more agreements to unlock ranking</p>
                ) : (
                  <>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-brand">{tech.renewalRate}%</p>
                      <p className="text-[12px] text-text-secondary">renewal rate</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{formatCurrency(tech.revenueRetained)}</p>
                      <p className="text-[12px] text-text-secondary">retained</p>
                    </div>
                  </>
                )}
                <Button variant="secondary" size="sm" onClick={() => setSelected(tech)}>
                  View at-risk book
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Users}
            title="No technicians yet"
            description="Add your team and assign them on agreements — insights appear once outcomes roll in."
            action={
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" /> Add technician
              </Button>
            }
          />
        )}
      </Card>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.name} — at-risk customers` : ''}
        description="Customers on this technician's book who need a renewal conversation this week."
      >
        {selected && (
          <div className="space-y-3">
            {atRiskLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : atRisk?.items.length ? (
              atRisk.items.map((item) => (
                <div key={item.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{item.contactName}</p>
                      <RiskBadge level={item.risk.level} />
                    </div>
                    <p className="text-[13px] text-text-secondary">
                      {item.planName} · {formatCurrency(item.amount)} · renews {item.relativeEndDate}
                    </p>
                  </div>
                  {item.contactPhone && (
                    <a href={`tel:${item.contactPhone}`}>
                      <Button variant="secondary" size="sm">
                        <Phone className="h-3.5 w-3.5" /> Call
                      </Button>
                    </a>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-text-secondary">No medium or high-risk customers on this book right now.</p>
            )}
          </div>
        )}
      </Modal>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add technician">
        <div className="space-y-4">
          <Input label="Name" value={techName} onChange={(e) => setTechName(e.target.value)} placeholder="Jordan Smith" />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button loading={addTech.isPending} disabled={!techName.trim()} onClick={() => addTech.mutate()}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
